import React from "react";
import { Controller, useFormState } from "react-hook-form";
import Select from "react-select";
import FormErrorMessage from "./FormErrorMessage";

export const FormHelperText = ({ children }) => (
  <div className="bg-dark-grey-lighter color-dark-grey-darker p-1">
    {children}
  </div>
);

const getStyles = (rowPosition) => {
  switch (rowPosition) {
    case true:
      return { backgroundColor: "hsl(0deg 0% 100% / 50%)" };
    case "first":
      return {
        backgroundColor: "hsl(0deg 0% 100% / 50%)",
        borderRadius: "4px 0 0 4px"
      };
    case "last":
      return {
        backgroundColor: "hsl(0deg 0% 100% / 50%)",
        borderRadius: "0 4px 4px 0"
      };
    default:
      return { backgroundColor: "transparent" };
  }
};

const normalizeMultiValue = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string")
    return value.split(",").map(v => v.trim()).filter(Boolean);
  return [];
};

const FormSelect = ({
  name,
  label,
  hideLabel,
  labelHelp,
  labelClassName = "",
  wrapperClassName = "",
  className = "",
  helperText,
  control,
  onChangeCallback,
  options = [],
  required = false,
  readOnly = false,
  isMulti = false,
  trigger,
  isClearable,
  rowPosition = false
}) => {
  const { errors } = useFormState({ control });

  return (
    <div className={`form-field ${wrapperClassName}`}>
      <label
        htmlFor={name}
        className={`form-label ${hideLabel ? "sr-only" : ""} ${labelClassName}`}
      >
        {required && <span className="form-layout-required">*</span>}
        {label}
        {labelHelp && <span className="font-italic ml-1">{labelHelp}</span>}
      </label>

      <Controller
        name={name}
        control={control}
        defaultValue={isMulti ? [] : null}
        render={({ field }) => {
          const multiValue = isMulti
            ? normalizeMultiValue(field.value)
            : field.value;

          const selectedValue = isMulti
            ? options.filter(opt => multiValue.includes(opt.value))
            : options.find(opt => opt.value === field.value) ?? null;

          return (
            <div
              className={rowPosition !== false ? "px-2 py-1" : ""}
              style={getStyles(rowPosition)}
            >
              <Select
                id={name}
                inputId={name} // required for aria-label
                aria-label={name}
                options={options}
                isMulti={isMulti}
                value={selectedValue}
                placeholder="Select..."
                isClearable={isClearable}
                isDisabled={readOnly}
                className={`reactSelect-wrapper ${errors[name] ? "is-invalid" : ""
                  } ${readOnly ? "reactSelect-readOnly" : ""} ${className} ${rowPosition !== false ? "mt-0" : ""
                  }`}
                classNamePrefix="reactSelect"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
                onChange={(selected) => {
                  if (isMulti) {
                    const values = selected
                      ? selected.map(opt => opt.value)
                      : [];
                    field.onChange(values);
                    onChangeCallback?.(values);
                  } else {
                    const value = selected?.value ?? null;
                    field.onChange(value);
                    onChangeCallback?.(value);
                  }

                  trigger?.(name);
                }}
              />
            </div>
          );
        }}
      />

      {errors[name] && (
        <FormErrorMessage message={errors[name].message} />
      )}

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </div>
  );
};

export default FormSelect;
